/** Action blocks (owner feedback F21): things people press. Every call names an action of the component. */
import { L, P, defineBlock } from './custom-blocks';
import { NOTIFY, ex } from './custom-blocks-kit';

const VARIANTS = ['primary', 'secondary', 'ghost', 'danger'] as const;
const VARIANT_LABELS = {
  primary: L('Primary', 'Primär'),
  secondary: L('Secondary', 'Sekundär'),
  ghost: L('Quiet', 'Dezent'),
  danger: L('Destructive', 'Löschend'),
};
const noteAction = {
  id: 'notify',
  name: L('Show a note', 'Hinweis zeigen'),
  steps: [{ kind: 'message', text: L('Done', 'Erledigt'), tone: 'success' }],
};

export const actionBlocks = {
  buttons: defineBlock({
    name: L('Button group', 'Schaltflächengruppe'),
    desc: L(
      'Several related buttons side by side, joined or apart.',
      'Mehrere zusammengehörige Schaltflächen nebeneinander, verbunden oder getrennt.',
    ),
    family: 'action',
    icon: 'buttons',
    keywords: ['toolbar', 'actions', 'buttons row', 'toggle group'],
    summary: 'A row of buttons; each item has label, icon, variant and the call of an action.',
    props: {
      items: P.items(
        'Buttons',
        'Schaltflächen',
        {
          label: P.tpl('Label', 'Beschriftung', { def: 'Button' }),
          icon: P.icon(),
          variant: P.pick('Style', 'Stil', VARIANTS, 'secondary', { labels: VARIANT_LABELS }),
          call: P.call('Runs', 'Führt aus'),
          disabledWhen: P.expr('Disabled when', 'Deaktiviert, wenn', { expect: 'boolean', opt: true }),
        },
        {
          titleKey: 'label',
          max: 8,
          min: 1,
          def: [{ label: 'Button' }],
          addLabel: L('Add a button', 'Schaltfläche hinzufügen'),
        },
      ),
      joined: P.bool('Joined as one control', 'Als ein Element verbunden', false),
      align: P.pick('Alignment', 'Ausrichtung', ['start', 'center', 'end'] as const, 'start', {
        seg: true,
        labels: { start: L('Left', 'Links'), center: L('Centre', 'Mitte'), end: L('Right', 'Rechts') },
      }),
    },
    example: {
      props: {
        items: [
          {
            label: L('Approve', 'Freigeben'),
            icon: 'check',
            variant: 'primary',
            call: { action: 'notify', args: {} },
          },
          { label: L('Reject', 'Ablehnen'), variant: 'secondary', call: { action: 'notify', args: {} } },
          { label: L('Later', 'Später'), variant: 'ghost' },
        ],
      },
      actions: [noteAction],
    },
  }),

  splitbutton: defineBlock({
    name: L('Split button', 'Split-Schaltfläche'),
    desc: L(
      'A main action with a menu of related actions beside it.',
      'Eine Hauptaktion mit einem Menü verwandter Aktionen daneben.',
    ),
    family: 'action',
    icon: 'chevron-down',
    keywords: ['dropdown button', 'more actions', 'primary action'],
    summary: 'A button that runs "call"; its arrow opens a menu of items, each calling an action.',
    props: {
      label: P.tpl('Label', 'Beschriftung', { def: 'Save' }),
      icon: P.icon(),
      variant: P.pick('Style', 'Stil', ['primary', 'secondary'] as const, 'primary', {
        seg: true,
        labels: { primary: L('Primary', 'Primär'), secondary: L('Secondary', 'Sekundär') },
      }),
      call: P.call('Main action', 'Hauptaktion'),
      items: P.items(
        'Menu',
        'Menü',
        {
          label: P.tpl('Label', 'Beschriftung', { def: 'Item' }),
          icon: P.icon(),
          call: P.call('Runs', 'Führt aus'),
          danger: P.bool('Destructive', 'Löschend', false),
        },
        { titleKey: 'label', max: 10, def: [], addLabel: L('Add an item', 'Eintrag hinzufügen') },
      ),
    },
    example: {
      props: {
        label: L('Submit', 'Einreichen'),
        variant: 'primary',
        call: { action: 'notify', args: {} },
        items: [
          {
            label: L('Save as draft', 'Als Entwurf sichern'),
            icon: 'file',
            call: { action: 'notify', args: {} },
          },
          { label: L('Discard', 'Verwerfen'), icon: 'trash', danger: true },
        ],
      },
      actions: [noteAction],
    },
  }),

  menu: defineBlock({
    name: L('Menu', 'Menü'),
    desc: L(
      'A button that opens a list of actions, with dividers and destructive items.',
      'Eine Schaltfläche, die eine Liste von Aktionen öffnet, mit Trennlinien und löschenden Einträgen.',
    ),
    family: 'action',
    icon: 'more',
    keywords: ['dropdown', 'overflow', 'context menu', 'more', 'kebab'],
    summary: 'A menu button. Items are actions; an item with no label and no call is a divider.',
    props: {
      label: P.tpl('Label', 'Beschriftung', { opt: true }),
      icon: P.icon(),
      variant: P.pick('Style', 'Stil', ['secondary', 'ghost'] as const, 'secondary', {
        seg: true,
        labels: { secondary: L('Outlined', 'Umrandet'), ghost: L('Quiet', 'Dezent') },
      }),
      items: P.items(
        'Items',
        'Einträge',
        {
          label: P.tpl('Label', 'Beschriftung', { opt: true }),
          icon: P.icon(),
          call: P.call('Runs', 'Führt aus'),
          danger: P.bool('Destructive', 'Löschend', false),
          disabledWhen: P.expr('Disabled when', 'Deaktiviert, wenn', { expect: 'boolean', opt: true }),
        },
        {
          titleKey: 'label',
          max: 16,
          def: [{ label: 'Item' }],
          addLabel: L('Add an item', 'Eintrag hinzufügen'),
        },
      ),
    },
    example: {
      props: {
        label: L('Actions', 'Aktionen'),
        icon: 'more',
        items: [
          { label: L('Duplicate', 'Duplizieren'), icon: 'duplicate', call: { action: 'notify', args: {} } },
          { label: L('Export', 'Exportieren'), icon: 'download', call: { action: 'notify', args: {} } },
          {},
          { label: L('Delete', 'Löschen'), icon: 'trash', danger: true },
        ],
      },
      actions: [noteAction],
    },
  }),

  confirmbutton: defineBlock({
    name: L('Confirm button', 'Bestätigungs-Schaltfläche'),
    desc: L(
      'A button that asks "Are you sure?" before it runs, for anything that cannot be undone.',
      'Eine Schaltfläche, die vor dem Ausführen nachfragt, für alles, was sich nicht zurücknehmen lässt.',
    ),
    family: 'action',
    icon: 'shield',
    keywords: ['delete', 'are you sure', 'dangerous', 'confirmation', 'dialog'],
    summary: 'A button that opens a confirmation dialog first; only after confirming it runs the call.',
    props: {
      label: P.tpl('Label', 'Beschriftung', { def: 'Delete' }),
      icon: P.icon(),
      variant: P.pick('Style', 'Stil', ['primary', 'secondary', 'danger'] as const, 'danger', {
        labels: VARIANT_LABELS,
      }),
      title: P.tpl('Question', 'Frage', { def: 'Are you sure?' }),
      text: P.tpl('Explanation', 'Erklärung', { opt: true, long: true }),
      confirmLabel: P.tpl('Confirm button', 'Bestätigen-Schaltfläche', { def: 'Yes, continue' }),
      call: P.call('Runs after confirming', 'Läuft nach der Bestätigung', { req: true }),
    },
    example: {
      props: {
        label: L('Cancel request', 'Antrag zurückziehen'),
        icon: 'trash',
        variant: 'danger',
        title: L('Cancel this request?', 'Diesen Antrag zurückziehen?'),
        text: L('Your manager will be told.', 'Ihre Führungskraft wird informiert.'),
        confirmLabel: L('Yes, cancel it', 'Ja, zurückziehen'),
        call: { action: 'notify', args: {} },
      },
      actions: [noteAction],
    },
  }),

  actionbar: defineBlock({
    name: L('Action bar', 'Aktionsleiste'),
    desc: L(
      'A bar of buttons with a status text, for the end of a form or a selection.',
      'Eine Leiste mit Schaltflächen und Statustext, für das Ende eines Formulars oder einer Auswahl.',
    ),
    family: 'action',
    icon: 'sliders',
    keywords: ['footer', 'form footer', 'bottom bar', 'bulk actions', 'save bar'],
    summary: 'A bar with a message on the left and its children (buttons) on the right.',
    children: 'many',
    props: {
      message: P.tpl('Message', 'Meldung', { opt: true }),
      look: P.pick('Look', 'Darstellung', ['plain', 'raised'] as const, 'raised', {
        seg: true,
        labels: { plain: L('Plain', 'Schlicht'), raised: L('Raised', 'Erhöht') },
      }),
    },
    example: {
      actions: [NOTIFY],
      props: { message: L('3 changes not sent yet', '3 Änderungen noch nicht gesendet') },
      children: [ex.button('Discard', 'Verwerfen', 'ghost'), ex.button('Send', 'Senden', 'primary')],
    },
  }),

  copy: defineBlock({
    name: L('Copy value', 'Wert kopieren'),
    desc: L(
      'A value with a button that copies it, for links, codes and ids.',
      'Ein Wert mit Schaltfläche zum Kopieren, für Links, Codes und Kennungen.',
    ),
    family: 'action',
    icon: 'copy',
    keywords: ['clipboard', 'share link', 'code', 'id', 'token'],
    summary: 'Shows the text of value in a field with a copy button.',
    props: {
      value: P.expr('Value', 'Wert', { expect: 'text', def: '' }),
      label: P.tpl('Label', 'Bezeichnung', { opt: true }),
      mono: P.bool('Monospaced', 'Festbreite', true),
    },
    example: {
      props: { label: L('Invite link', 'Einladungslink'), value: '"https://example.com/invite/7KQ-2F9"' },
    },
  }),
};
